import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { BiReset, BiSearch, BiSolidInbox } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { SALES_MANAGE_ROLES, SALES_RESTORE_ROLES, SALES_ROLES, hasRole } from '@/auth/roles';
import { listLeads, restoreLead } from '@/api/leads.api';
import { ApiError } from '@/api/errors';
import {
  DataTable,
  type ActiveFilterChip,
  type DataTableColumn,
  type DataTableSortState,
} from '@/components/table/DataTable';
import { EmptyState } from '@/components/table/EmptyState';
import { Banner } from '@/components/common/Banner';
import { StatusChip } from '@/components/common/StatusChip';
import { showSuccessToast } from '@/lib/toast';
import { formatDateTime } from '@/lib/format';
import { toneForLeadStatus } from '@/lib/leadStatus';
import { LEAD_SOURCES, LEAD_STATUSES } from '@/types/shared';
import type { LeadResponse, LeadSource, LeadStatus, PaginatedLeads } from '@/types/shared';
import './LeadsListPage.css';

export function LeadsListPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canCreate = hasRole(user?.role, SALES_ROLES);
  const canSeeUnassignedQueue = hasRole(user?.role, SALES_MANAGE_ROLES);
  const canRestore = hasRole(user?.role, SALES_RESTORE_ROLES);
  const successMessage = (location.state as { successMessage?: string } | null)?.successMessage;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<DataTableSortState>({ field: null, direction: 'asc' });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [sourceFilter, setSourceFilter] = useState<LeadSource | ''>('');
  const [unassignedOnly, setUnassignedOnly] = useState(false);

  const [data, setData] = useState<PaginatedLeads | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const [restoreId, setRestoreId] = useState('');
  const [restoreSubmitting, setRestoreSubmitting] = useState(false);
  const [restoreMessage, setRestoreMessage] = useState<{
    tone: 'success' | 'danger';
    text: string;
  } | null>(null);

  // Debounce the free-text search box before it drives the `search` query param.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Any filter/search change re-starts pagination at page 1 (sort changes keep the current page).
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, sourceFilter, unassignedOnly]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const sortParam = sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined;

    listLeads({
      page,
      pageSize,
      sort: sortParam,
      search: search || undefined,
      status: statusFilter || undefined,
      leadSource: sourceFilter || undefined,
      unassigned: canSeeUnassignedQueue && unassignedOnly ? true : undefined,
    })
      .then((result) => {
        if (cancelled) return;
        setData(result);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t('leads.error.loadFailed'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    sort,
    search,
    statusFilter,
    sourceFilter,
    unassignedOnly,
    canSeeUnassignedQueue,
    t,
    reloadToken,
  ]);

  const activeFilterCount = [
    Boolean(search),
    statusFilter !== '',
    sourceFilter !== '',
    unassignedOnly,
  ].filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0;

  function clearFilters() {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setSourceFilter('');
    setUnassignedOnly(false);
  }

  // 14-tables-forms-restyle-spec.md §3.3 — `unassignedOnly` included (genuinely narrows results).
  const activeFilterChips: ActiveFilterChip[] = [
    statusFilter !== '' && {
      key: 'status',
      label: t('leads.filters.status'),
      valueLabel: t(`leads.status.${statusFilter}`),
      onRemove: () => setStatusFilter(''),
    },
    sourceFilter !== '' && {
      key: 'source',
      label: t('leads.filters.source'),
      valueLabel: t(`leads.source.${sourceFilter}`),
      onRemove: () => setSourceFilter(''),
    },
    canSeeUnassignedQueue &&
      unassignedOnly && {
        key: 'unassignedOnly',
        label: t('leads.filters.unassignedOnly'),
        valueLabel: t('leads.filters.unassignedOnly'),
        onRemove: () => setUnassignedOnly(false),
      },
  ].filter(Boolean) as ActiveFilterChip[];

  async function handleRestoreSubmit(e: FormEvent) {
    e.preventDefault();
    const id = restoreId.trim();
    if (!id) return;
    setRestoreSubmitting(true);
    setRestoreMessage(null);
    try {
      await restoreLead(id);
      const message = t('leads.restore.success');
      setRestoreMessage({ tone: 'success', text: message });
      showSuccessToast(message);
      setRestoreId('');
      setReloadToken((v) => v + 1);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      const text =
        apiError.code === 'NOT_FOUND'
          ? t('leads.restore.notFound')
          : t('leads.restore.genericError');
      setRestoreMessage({ tone: 'danger', text });
    } finally {
      setRestoreSubmitting(false);
    }
  }

  const columns: DataTableColumn<LeadResponse>[] = [
    {
      key: 'lead',
      header: t('leads.columns.lead'),
      sortField: 'fullName',
      // Primary identifier column — always rendered, never hidden (14-...-spec.md §4.2).
      toggleable: false,
      render: (row) => (
        <span className="fa-leads-list__lead-cell">
          <span className="fa-leads-list__lead-name">{row.fullName}</span>
          <bdi className="fa-leads-list__lead-phone fa-ltr-isolate">{row.phone}</bdi>
        </span>
      ),
    },
    {
      key: 'status',
      header: t('leads.columns.status'),
      sortField: 'status',
      render: (row) => (
        <StatusChip label={t(`leads.status.${row.status}`)} tone={toneForLeadStatus(row.status)} />
      ),
    },
    {
      key: 'assignedTo',
      header: t('leads.columns.assignedTo'),
      render: (row) =>
        row.assignedEmployee ? (
          row.assignedEmployee.fullName
        ) : (
          <span className="fa-leads-list__muted">{t('leads.unassignedLabel')}</span>
        ),
    },
    {
      key: 'source',
      header: t('leads.columns.source'),
      render: (row) => t(`leads.source.${row.leadSource}`),
    },
    {
      key: 'created',
      header: t('leads.columns.created'),
      sortField: 'createdAt',
      render: (row) => <bdi>{formatDateTime(row.createdAt, i18n.language)}</bdi>,
    },
  ];

  return (
    <section className="fa-leads-list">
      {successMessage && <Banner tone="success">{successMessage}</Banner>}
      <div className="fa-leads-list__header">
        <div>
          <h1 className="fa-leads-list__title">{t('leads.listTitle')}</h1>
          <p className="fa-leads-list__subtitle">
            {data ? t('leads.resultCount', { count: data.meta.total }) : ' '}
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            className="fa-btn fa-btn--primary"
            onClick={() => navigate('/leads/new')}
          >
            {t('leads.newLeadButton')}
          </button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        rowKey={(row) => row.leadId}
        caption={t('leads.tableCaption')}
        meta={data?.meta ?? null}
        loading={loading}
        error={error}
        onRetry={() => setReloadToken((v) => v + 1)}
        onRowClick={(row) => navigate(`/leads/${row.leadId}`)}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('leads.searchPlaceholder'),
          ariaLabel: t('leads.searchAriaLabel'),
        }}
        sort={sort}
        onSortChange={setSort}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        exportConfig={{
          endpoint: '/leads/export',
          // Forward the SAME active filters as the on-screen list so the workbook matches what the
          // user sees (own-leads RBAC scope is always enforced server-side regardless).
          params: {
            sort: sort.field ? `${sort.field}:${sort.direction.toUpperCase()}` : undefined,
            search: search || undefined,
            status: statusFilter || undefined,
            leadSource: sourceFilter || undefined,
            unassigned: canSeeUnassignedQueue && unassignedOnly ? true : undefined,
          },
          fallbackFilename: 'Leads_export.xlsx',
        }}
        columnVisibility={{
          storageKey: 'leads-table-columns',
          ariaLabel: t('table.columns.toggleButtonAriaLabel'),
          drawerTitle: t('table.columns.drawerTitleDefault'),
        }}
        activeFilters={activeFilterChips}
        onClearAllFilters={clearFilters}
        filtersDrawerTitle={t('leads.filters.groupLabel')}
        filtersSlot={
          <div className="fa-leads-list__filters-group">
            <label className="fa-leads-list__filter">
              <span className="fa-leads-list__filter-label">{t('leads.filters.status')}</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as LeadStatus | '')}
              >
                <option value="">{t('leads.filters.allStatuses')}</option>
                {LEAD_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`leads.status.${status}`)}
                  </option>
                ))}
              </select>
            </label>

            <label className="fa-leads-list__filter">
              <span className="fa-leads-list__filter-label">{t('leads.filters.source')}</span>
              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value as LeadSource | '')}
              >
                <option value="">{t('leads.filters.allSources')}</option>
                {LEAD_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {t(`leads.source.${source}`)}
                  </option>
                ))}
              </select>
            </label>

            {canSeeUnassignedQueue && (
              <label className="fa-leads-list__filter fa-leads-list__filter--checkbox">
                <input
                  type="checkbox"
                  checked={unassignedOnly}
                  onChange={(e) => setUnassignedOnly(e.target.checked)}
                />
                {t('leads.filters.unassignedOnly')}
              </label>
            )}
          </div>
        }
        emptyState={
          hasActiveFilters ? (
            <EmptyState
              icon={BiSearch}
              title={t('leads.empty.noMatchesTitle')}
              hint={t('leads.empty.noMatchesHint')}
              action={
                <button type="button" className="fa-btn fa-btn--secondary" onClick={clearFilters}>
                  {t('leads.filters.clear')}
                </button>
              }
            />
          ) : (
            <EmptyState
              icon={BiSolidInbox}
              title={t('leads.empty.noRecordsTitle')}
              hint={t('leads.empty.noRecordsHint')}
              action={
                canCreate && (
                  <button
                    type="button"
                    className="fa-btn fa-btn--primary"
                    onClick={() => navigate('/leads/new')}
                  >
                    {t('leads.newLeadButton')}
                  </button>
                )
              }
            />
          )
        }
      />

      {canRestore && (
        <div className="fa-leads-list__restore">
          <div className="fa-leads-list__restore-heading">
            <span className="fa-leads-list__restore-icon" aria-hidden="true">
              <BiReset />
            </span>
            <div>
              <h2 className="fa-leads-list__restore-title">{t('leads.restore.sectionTitle')}</h2>
              <p className="fa-leads-list__restore-hint">{t('leads.restore.hint')}</p>
            </div>
          </div>
          {restoreMessage && (
            <Banner tone={restoreMessage.tone} assertive={restoreMessage.tone === 'danger'}>
              {restoreMessage.text}
            </Banner>
          )}
          <form className="fa-leads-list__restore-form" onSubmit={handleRestoreSubmit}>
            <label>
              <span className="fa-visually-hidden">{t('leads.restore.idAriaLabel')}</span>
              <input
                type="text"
                className="fa-ltr-isolate"
                value={restoreId}
                placeholder={t('leads.restore.idPlaceholder')}
                disabled={restoreSubmitting}
                onChange={(e) => setRestoreId(e.target.value)}
              />
            </label>
            <button type="submit" className="fa-btn fa-btn--secondary" disabled={restoreSubmitting}>
              {t('leads.restore.submit')}
            </button>
          </form>
        </div>
      )}
    </section>
  );
}
